import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { Grid, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DrawerCustom from '../../shared/general/Drawer';
import dayjs from 'dayjs';
import DatePickerCustom from '../../shared/date/datePicker';
import ButtonBlue from '../../shared/general/ButtonBlue';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { getTimeAttendance } from '../../../../actions/report';
import AlertResponse from '../../shared/general/AlertResponse';
import { AttendanceXlsxReport } from '../../vendor/report/xlsx-export/attendanceXlsxReport';
import { useSelector } from 'react-redux';
import CompanyService from '../../../../services/company.service';

const StyledRoot = styled("div")({
    maxWidth: 550,
    padding: 24,
});

const StyledFooter = styled("div")({
    padding: 16,
    display: "flex",
    justifyContent: "flex-end",
    "& .cancel": {
        marginRight: 8,
    },
});

export default function Attendance(props) {
    const { open, handleClose } = props;
    const { t } = useTranslation();
    const today = dayjs().toDate();

    const { result: userProfile } = useSelector((state) => state.userProfile);

    const [idVendor, setIdVendor] = useState(null);
    const [openAlert, setOpenAlert] = useState(false);
    const [alertType, setAlertType] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const [search, setSearch] = useState({
        start: dayjs(today).set("date", 1),
        end: dayjs(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
    });

    const handleOpenAlert = () => {
        setOpenAlert(true);
    };

    const handleCloseAlert = () => {
        setOpenAlert(false);
    };

    const handleChangeAlertType = (newValue) => {
        setAlertType(newValue);
    };

    useEffect(() => {
        const fetchVendorContract = async () => {
            try {
                const response = await CompanyService.getVendorContract();
                if (response && response.data && response.data.length > 0) {
                    setIdVendor(response.data[0].idVendor);
                }
            } catch (error) {
                console.error(error);
            }
        };

        fetchVendorContract();
    }, []);

    const handleDownloadFile = async () => {
        if (!userProfile || !idVendor) return;
        try {
            setIsFetching(true);
            const response = await getTimeAttendance({
                start: dayjs(search.start).format("YYYY-MM-DD"),
                end: dayjs(search.end).format("YYYY-MM-DD"),
                idCompany: userProfile.idCompany,
                idVendor: idVendor,
            });
            if (response && response.data && response.data.result && response.data.result.length > 0) {
                AttendanceXlsxReport(t, response.data.result, response.data.leaveList);
            } else {
                handleChangeAlertType(t("NoData"));
                handleOpenAlert(true);
            }
        } catch (error) {
            console.error(error);
            handleChangeAlertType(t("NoData"));
            handleOpenAlert(true);
        } finally {
            setIsFetching(false);
        }
    };

    return (
        <DrawerCustom
            title={`${t("attendanceReport")}`}
            anchor="right"
            open={open}
            onClose={handleClose}
        >
            <StyledRoot style={{ width: 400 }}>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Typography color="text.third" fontSize="14px" fontWeight="600" marginBottom="8px">
                            {t("StartDate")}
                        </Typography>
                        <DatePickerCustom
                            minDate={new Date(new Date().getFullYear() - 4, 0, 1)}
                            inputFormat="DD/MM/YYYY"
                            value={search.start}
                            name="start"
                            disabled={isFetching}
                            onChange={(newValue) => {
                                setSearch({ ...search, ["start"]: newValue });
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Typography color="text.third" fontSize="14px" fontWeight="600" marginBottom="8px">
                            {t("EndDate")}
                        </Typography>
                        <DatePickerCustom
                            minDate={search.start}
                            inputFormat="DD/MM/YYYY"
                            value={search.end}
                            name="end"
                            disabled={isFetching}
                            onChange={(newValue) => {
                                setSearch({ ...search, ["end"]: newValue });
                            }}
                        />
                    </Grid>
                </Grid>

                <StyledFooter>
                    <ButtonBlue className="cancel" onClick={handleClose}>
                        {t("Cancel")}
                    </ButtonBlue>

                    {isFetching ? (
                        <CircularProgress />
                    ) : (
                        <ButtonBlue
                            variant="outlined"
                            startIcon={<DownloadRoundedIcon />}
                            onClick={() => handleDownloadFile()}
                            disabled={isFetching || !idVendor}
                        >
                            {t("Download")}
                        </ButtonBlue>
                    )}
                </StyledFooter>
            </StyledRoot>
            <AlertResponse
                open={openAlert}
                handleClose={handleCloseAlert}
                alertType={alertType}
            />
        </DrawerCustom>
    );
}
